/**
 * FERP Portal - Notification Badge Component (DRR-233: Track C)
 *
 * Displays unread notification count in the app header.
 * Uses getUnreadCount query to show a badge on the bell icon.
 *
 * FERP Principles:
 * - Backend-driven: count comes from backend, not client-side filter
 * - Real-time ready: supports subscription for live updates (future)
 */

import { useRef } from 'react';
import { ActionIcon, Badge, Indicator, Tooltip } from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useDevActorId } from '@/hooks/useDevActorId';
import { useNavigation } from '@/contexts/NavigationContext';

// Query for unread notification count
const GET_UNREAD_COUNT = gql`
  query GetUnreadCount($recipientActorId: String!) {
    getUnreadCount(recipientActorId: $recipientActorId)
  }
`;

interface NotificationBadgeProps {
  /** Path to navigate to when clicked. Defaults to /ferp-core/inbox */
  inboxPath?: string;
}

export function NotificationBadge({ inboxPath = '/ferp-core/inbox' }: NotificationBadgeProps) {
  const navigate = useNavigate();
  const { actorId } = useDevActorId();
  const { hasAnyScope, loading: navLoading } = useNavigation();

  // Track if we've had an auth error - stop polling on failure
  const hasAuthError = useRef(false);

  // Check scope before querying - requires notification:read or notification:admin
  const hasNotificationScope = hasAnyScope(['notification:read', 'notification:admin']);

  const { data, loading } = useQuery<{ getUnreadCount: number }>(GET_UNREAD_COUNT, {
    variables: { recipientActorId: actorId ?? '' },
    skip: !actorId || navLoading || !hasNotificationScope || hasAuthError.current,
    pollInterval: hasAuthError.current ? 0 : 30000, // Stop polling on auth error
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      // Stop retrying on auth/scope errors
      if (error.message.includes('Forbidden') || error.message.includes('FERP-5002')) {
        hasAuthError.current = true;
      }
    },
  });

  const unreadCount = data?.getUnreadCount ?? 0;

  const handleClick = () => {
    navigate(inboxPath);
  };

  // Show indicator only when there are unread notifications
  if (unreadCount > 0) {
    return (
      <Indicator
        inline
        label={unreadCount > 99 ? '99+' : unreadCount}
        size={16}
        color="red"
        processing={loading}
        withBorder
      >
        <Tooltip label={`${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`}>
          <ActionIcon
            variant="light"
            size="lg"
            aria-label={`Notifications (${unreadCount} unread)`}
            onClick={handleClick}
          >
            <IconBell size={18} />
          </ActionIcon>
        </Tooltip>
      </Indicator>
    );
  }

  // No unread notifications - simple bell icon
  return (
    <Tooltip label="Notifications">
      <ActionIcon
        variant="light"
        size="lg"
        aria-label="Notifications"
        onClick={handleClick}
      >
        <IconBell size={18} />
      </ActionIcon>
    </Tooltip>
  );
}

export default NotificationBadge;
